import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import LevelUpModal from './LevelUpModal';
import TrophyEarnedModal from './TrophyEarnedModal';

interface TrophyInfo {
  key: string;
  name: string;
  tier: number;
  bonusXp: number;
}

interface ProgressionContextValue {
  showLevelUp: (newLevel: number) => void;
  showTrophies: (trophies: TrophyInfo[]) => void;
}

const ProgressionContext = createContext<ProgressionContextValue>({
  showLevelUp: () => {},
  showTrophies: () => {},
});

export function useProgressionNotify() {
  return useContext(ProgressionContext);
}

interface ProgressionProviderProps {
  children: ReactNode;
}

export default function ProgressionProvider({ children }: ProgressionProviderProps) {
  const [levelUpData, setLevelUpData]     = useState<{ newLevel: number } | null>(null);
  const [trophyData, setTrophyData]       = useState<TrophyInfo[] | null>(null);

  const showLevelUp = useCallback((newLevel: number) => {
    setLevelUpData({ newLevel });
  }, []);

  const showTrophies = useCallback((trophies: TrophyInfo[]) => {
    if (trophies.length > 0) setTrophyData(trophies);
  }, []);

  const closeLevelUp = useCallback(() => {
    setLevelUpData(null);
  }, []);

  const closeTrophies = useCallback(() => {
    setTrophyData(null);
  }, []);

  return (
    <ProgressionContext.Provider value={{ showLevelUp, showTrophies }}>
      {children}

      {levelUpData && (
        <LevelUpModal newLevel={levelUpData.newLevel} onClose={closeLevelUp} />
      )}

      {/* Show trophy modal after level-up closes (not both at same time) */}
      {!levelUpData && trophyData && (
        <TrophyEarnedModal trophies={trophyData} onClose={closeTrophies} />
      )}
    </ProgressionContext.Provider>
  );
}
